import { ApiError, type ApiErrorKind } from "../api-error";
import type {
  ApiProblem,
  ApiRequest,
  ApiResponse,
  ApiTransport,
} from "../types";

interface FetchTransportOptions {
  basePath: string;
  defaultHeaders?: HeadersInit;
}

function resolveErrorKind(status: number): ApiErrorKind {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not-found";
  if (status === 409 || status === 412) return "conflict";
  if (status >= 500) return "unavailable";
  return "rejected";
}

function createUrl(basePath: string, path: string): string {
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("://")) {
    throw new TypeError("API paths must be root-relative paths");
  }

  return `${basePath.replace(/\/$/, "")}${path}`;
}

async function readResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;

  const contentType = response.headers.get("content-type");
  if (!contentType?.includes("application/json")) return undefined;

  return response.json();
}

export class FetchTransport implements ApiTransport {
  constructor(private readonly options: FetchTransportOptions) {}

  async send<TData, TBody>(
    request: ApiRequest<TBody>,
  ): Promise<ApiResponse<TData>> {
    const headers = new Headers(this.options.defaultHeaders);
    headers.set("Accept", "application/json");

    if (request.body !== undefined)
      headers.set("Content-Type", "application/json");
    if (request.idempotencyKey)
      headers.set("Idempotency-Key", request.idempotencyKey);
    if (request.expectedVersion)
      headers.set("If-Match", request.expectedVersion);
    if (request.antiforgeryToken)
      headers.set("X-CSRF-TOKEN", request.antiforgeryToken);

    const response = await fetch(
      createUrl(this.options.basePath, request.path),
      {
        method: request.method,
        credentials: "include",
        cache: "no-store",
        headers,
        body:
          request.body === undefined ? undefined : JSON.stringify(request.body),
        signal: request.signal,
      },
    );
    const responseBody = await readResponseBody(response);
    const correlationId = response.headers.get("x-correlation-id") ?? undefined;

    if (!response.ok) {
      const problem = (responseBody ?? {}) as ApiProblem;
      throw new ApiError(problem.title ?? "API request rejected", {
        kind: resolveErrorKind(response.status),
        status: response.status,
        code: problem.code,
        correlationId: problem.correlationId ?? correlationId,
        problem,
      });
    }

    return {
      data: responseBody as TData,
      correlationId,
      etag: response.headers.get("etag") ?? undefined,
      status: response.status,
    };
  }
}
